/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-evaluation-models.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import Button from '@components/core/buttons/button';
import Loading from '@components/core/loading';
import PageTitle from '@components/core/page-title/page-title';
import Error from '@components/error/error';
import EditEvaluationModel from '@pages/admin/organisations/admin-evaluation-models/edit-evaluation-model';
import EvaluationModelsGrid from '@pages/admin/organisations/admin-evaluation-models/evaluation-models-grid/evaluation-models-grid';
import { useBoolean, useTranslation, useAuthorization } from '@hooks/index';
import NotificationService from '@utils/notification';
import { getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel } from '@queries/generated/legacy-risk-assessment.generated';
import { GET_RISK_EVALUATION_MODELS } from '@queries/legacy-risk-assessment';

const AdminEvaluationModels = () => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const showEditEvalModel = useBoolean(false);
    const isLoadedFromQuery = useBoolean(false);
    const [currentModel, setCurrentModel] = useState(null);
    const [items, setItems] = useState(null);
    const [organisationId, setOrganisationId] = useState(null);
    const [loadModels, { loading, error, data }] = useLazyQuery(GET_RISK_EVALUATION_MODELS);
    const canEdit = user?.canWriteSystemAdminLevel();

    const getMax = (values: any) =>
        Math.max.apply(
            null,
            values?.filter((v: any) => v.enabled).map((v: any) => v.value),
        );

    const formatArray = (res: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel[]) => {
        const array = res.map((item: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel) => ({
            ...item,
            likelihoodUpper: getMax(item.likelihood) ?? 0,
            impactUpper: getMax(item.impact) ?? 0,
            detectabilityUpper: !item.includeDetectability ? 0 : (getMax(item.detectability) ?? 0),
            includeDetectability: item.includeDetectability,
            ragMap: item.ragMap.map((el: any) => ({
                ...el,
                colour:
                    typeof el.colour === 'number'
                        ? el.colour === 0
                            ? 'RED'
                            : el.colour === 1
                              ? 'AMBER'
                              : 'GREEN'
                        : el.colour,
            })),
        }));
        return [...array];
    };

    useEffect(() => {
        setOrganisationId(user.organisationId);
        isLoadedFromQuery.setFalse();
    }, [user?.organisationId]);

    useEffect(() => {
        const isSameOrg =
            user?.organisation?.evaluationModels.filter(
                (el: any) => data?.riskEvaluationModels?.findIndex((item: any) => el.id === item.id) >= 0,
            ).length > 0;
        if (data?.riskEvaluationModels && !loading && isSameOrg) {
            isLoadedFromQuery.setTrue();
            const array = formatArray(data?.riskEvaluationModels);
            user.setUser({
                ...user,
                organisation: {
                    ...user.organisation,
                    evaluationModels: data?.riskEvaluationModels,
                },
            });
            setItems(array);
        }
    }, [data, user?.organisation?.evaluationModels]);

    useEffect(() => {
        if (user?.organisation?.evaluationModels && !isLoadedFromQuery.value)
            setItems(formatArray(user?.organisation?.evaluationModels));
        else isLoadedFromQuery.setFalse();
    }, [user?.organisation?.evaluationModels]);

    if (!user || !organisationId || !user.isOrgAdmin()) return null;

    if (loading) return <Loading float />;

    if (error) return <Error error={error} />;

    return (
        <>
            <EditEvaluationModel
                evaluationModelId={currentModel?.id}
                open={showEditEvalModel.value}
                data={items ?? []}
                onClose={(action: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'edit')
                            NotificationService.showNotification(
                                t('ra:successfulUpdateEvaluationModel', {
                                    id: currentModel?.recordId ?? currentModel?.id,
                                }),
                            );
                        else if (action === 'add')
                            NotificationService.showNotification(t('ra:successfulCreateEvaluationModel'));
                        setCurrentModel(null);
                        showEditEvalModel.setFalse();
                        loadModels();
                    }
                }}
            />
            <PageTitle text={t('ra:riskEvaluationModelAdministration')} />
            <Button
                text="ra:addRiskEvaluationModel"
                onClick={() => showEditEvalModel.setTrue()}
                disabled={!canEdit}
                style={{ marginLeft: 'auto', display: 'flex', marginTop: 20, marginBottom: 20 }}
            />
            <EvaluationModelsGrid
                data={items}
                rowActions={(item: getRiskEvaluationModels_OpraQueries_riskEvaluationModels_RiskEvaluationModel) => [
                    {
                        label: 'ra:viewEditModel',
                        icon: ['fal', 'edit'],
                        onClick: () => {
                            setCurrentModel(item);
                            showEditEvalModel.setTrue();
                        },
                        enabled: canEdit,
                    },
                ]}
            />
        </>
    );
};

export default AdminEvaluationModels;
